import { Attributes, Misc, ModelBase, Rules, Model, Validation } from '@singularsystems/neo-core';
import Address from './Address';
import { RelationshipType } from './Enums/RelationshipType';
import { IDTypeId } from './Enums/IDTypeId';
import SAIDValidator from '../Services/SAIDValidator';

export default class Beneficiary extends ModelBase {
    static typeName = "Beneficiary";

    constructor() {
        super();
        this.makeObservable();
    }

    public beneficiaryId: number = 0;

    @Attributes.Serialisation(Misc.SerialiseType.NotNull)
    public participantId: string | null = null;

    @Rules.Required()
    @Rules.StringLength(50)
    public firstName: string = "";

    @Rules.Required()
    @Rules.StringLength(100)
    public lastName: string = "";

    @Rules.Required()
    @Attributes.Date(Misc.TimeZoneFormat.None)
    public dateOfBirth: Date | null = null;

    @Rules.Required("ID Type is required")
    @Attributes.Nullable()
    public idTypeId: number | null = null;

    @Rules.Required("ID Number is required")
    @Rules.StringLength(50)
    public idNumber: string = "";

    @Rules.Required()
    public relationship: RelationshipType | null = null;

    @Rules.EmailAddress()
    @Attributes.Display("Email")
    public email: string = "";

    @Rules.RegExp(new RegExp("^(((00|\\+)\\d{1,3}\\s?)|0)\\(?\\d{1,3}\\)?[\\s-.]?(\\d[\\s-.]?){6,9}$"), "Invalid mobile number.")
    public mobileNumber: string = "";

    @Rules.Required()
    @Attributes.ChildObject(Address, true)
    public postalAddress: Address = new Address();

    @Attributes.Float()
    public sharePercentage: number = 0;

    public isGuardian: boolean = false;

    @Attributes.ChildObject(() => Beneficiary)
    public guardian: Beneficiary | null = null;

    @Attributes.ChildObject(Model.AuditValues)
    public audit: Model.AuditValues | null = null;

    @Attributes.Observable()
    public isExpanded: boolean = false;

    @Attributes.Computed()
    public get ageInYears(): number {
        if (!this.dateOfBirth) return 0;
        const dob = new Date(this.dateOfBirth);
        const today = new Date();
        let age = today.getFullYear() - dob.getFullYear();
        const m = today.getMonth() - dob.getMonth();
        if (m < 0 || (m === 0 && today.getDate() < dob.getDate())) {
            age--;
        }
        return age;
    }

    @Attributes.Computed()
    public get isMinor(): boolean {
        return !!this.dateOfBirth && this.ageInYears < 18;
    }

    @Attributes.Display("Is Minor")
    public get isMinorDisplay(): string {
        return this.isMinor ? "Yes" : "No";
    }

    @Attributes.Computed()
    public get expandState(): boolean | null {
        return this.guardian ? this.isExpanded : null;
    }
    public set expandState(value: boolean | null) {
        this.isExpanded = !!value;
    }

    protected static addBusinessRules(rules: Validation.Rules<Beneficiary>) {
        super.addBusinessRules(rules);
        rules.failWhen(c => c.sharePercentage > 100, "Share percentage cannot be above 100%.");

        rules.failWhen(
            c => {
                if (c.idTypeId === IDTypeId.SAIDDocument && c.idNumber) {
                    const validationResult = SAIDValidator.checkIfIdNumberIsValid(c.idNumber, { minimumAgeInYears: c.isGuardian ? 18 : 0 });
                    return !validationResult.isValid;
                }
                return false;
            },
            c => {
                if (c.idTypeId === IDTypeId.SAIDDocument && c.idNumber) {
                    const validationResult = SAIDValidator.checkIfIdNumberIsValid(c.idNumber, { minimumAgeInYears: c.isGuardian ? 18 : 0 });
                    return validationResult.errorString;
                }
                return "";
            }
        ).onProperties(c => [c.idNumber, c.idTypeId]);

        rules.failWhen(
            c => !c.isGuardian && c.isMinor && !c.guardian,
            "A minor beneficiary requires a guardian."
        ).onProperties(c => [c.dateOfBirth]);

        rules.failWhen(
            c => c.isGuardian && c.isMinor,
            "A guardian must be at least 18 years old."
        ).onProperties(c => [c.dateOfBirth]);
    }

    public toString(): string {
        if (this.isNew || !this.participantId) {
            return "New beneficiary";
        } else {
            return this.participantId;
        }
    }
}
